import { useAuth } from "@dl/web-core-js/auth";
import { Badge, Button, cn, Dialog, Field, Textarea, toast } from "@dl/web-core-js/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { CheckCircle2, PauseCircle, Send, Undo2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { refusalMessage } from "../../../api/manufacturing";
import { PurchaseOrderClient, type PurchaseOrderDTO } from "../../../api/purchasing";
import { replenishmentKeys } from "../../../api/replenishment";
import { SupplierLink } from "../../../components/SupplierLink";
import { formatCad } from "./shared";

/**
 * Orders that have not reached a supplier yet: drafts, and orders waiting for
 * approval.
 *
 * <p>A draft is issued from here without opening it — to the supplier, or on
 * hold until its truck is full. At or above the approval threshold, issuing
 * sends it to the approver instead, and it moves to the next tab. The approver
 * approves or sends back from that tab; sending back needs a reason, because
 * whoever wrote the order has to know what to change.</p>
 */
export function OrdersTab({ status, canApprove }: { status: "DRAFT" | "AWAITING_APPROVAL"; canApprove: boolean }) {
  const { t } = useTranslation();
  const { http, canWrite } = useAuth();
  const queryClient = useQueryClient();
  const orders = useMemo(() => new PurchaseOrderClient(http), [http]);
  const [sendingBack, setSendingBack] = useState<PurchaseOrderDTO | null>(null);

  const list = useQuery({
    queryKey: ["purchasing", "purchase-order", "status", status],
    queryFn: async () => (await orders.find({ size: 500, filters: { status: [status] } })).content,
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["purchasing", "purchase-order"] });
    void queryClient.invalidateQueries({ queryKey: replenishmentKeys.all });
  };
  const failed = (failure: unknown) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong.")));

  const issue = useMutation({
    mutationFn: ({ id, onHold }: { id: number; onHold: boolean }) => orders.issue(id, onHold),
    onSuccess: (order) => {
      toast.success(
        order.status === "AWAITING_APPROVAL"
          ? t("replenishment.orders.sent-to-approval", "#{{number}} is at or above the threshold: sent for approval.", { number: order.number })
          : order.is_on_hold
            ? t("replenishment.orders.issued-held", "#{{number}} issued on hold: release it when the truck is full.", { number: order.number })
            : t("replenishment.orders.issued", "#{{number}} issued to {{supplier}}.", { number: order.number, supplier: order.supplier_name ?? "" }),
      );
      refresh();
    },
    onError: failed,
  });

  const approve = useMutation({
    mutationFn: (id: number) => orders.approve(id),
    onSuccess: (order) => {
      toast.success(t("replenishment.orders.approved", "#{{number}} approved and issued.", { number: order.number }));
      refresh();
    },
    onError: failed,
  });

  const rows = list.data ?? [];
  const total = rows.reduce((sum, order) => sum + Number(order.total_cad ?? 0), 0);

  return (
    <div className="min-h-0 flex-1 overflow-auto bg-ground px-2 py-3 sm:px-4 lg:px-6">
      {list.isError ? (
        <p className="m-2 rounded-md bg-risk-soft px-3 py-2 text-[13px] text-risk">
          {t("replenishment.orders.error", "The orders could not be loaded.")}
        </p>
      ) : list.isLoading ? (
        <p className="p-6 text-[13px] text-ink-muted">{t("table.loading", "Loading…")}</p>
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center gap-2 px-6 py-16 text-center">
          <CheckCircle2 className="size-8 text-ok" />
          <h3 className="text-[15px] font-semibold text-ink">
            {status === "DRAFT"
              ? t("replenishment.orders.no-drafts", "No drafts")
              : t("replenishment.orders.no-approval", "Nothing waiting for approval")}
          </h3>
          <p className="max-w-sm text-[13px] text-ink-muted">
            {status === "DRAFT"
              ? t("replenishment.orders.no-drafts.body", "Select materials in To order and create draft orders.")
              : t("replenishment.orders.no-approval.body", "Orders at or above the approval threshold wait here once issued.")}
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <p className="px-1 text-[12px] text-ink-muted">
            {t("replenishment.orders.total", "{{count}} orders · {{amount}}", { count: rows.length, amount: formatCad(total) })}
            {status === "AWAITING_APPROVAL" && !canApprove && (
              <> · {t("replenishment.orders.not-approver", "Only an approver can approve or send back.")}</>
            )}
          </p>
          <ul className="overflow-hidden rounded-xl border border-line bg-surface shadow-card">
            {rows.map((order) => (
              <li
                key={order.id}
                className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-line-soft px-3 py-3 last:border-b-0 sm:px-4"
              >
                <div className="flex min-w-0 flex-1 flex-col">
                  <div className="flex flex-wrap items-center gap-2">
                    <Link
                      to="/purchasing/purchase-orders/$orderId"
                      params={{ orderId: String(order.id) }}
                      className="font-mono text-[13px] font-semibold text-ink hover:text-accent"
                    >
                      #{order.number}
                    </Link>
                    <SupplierLink id={order.supplier_id} className="truncate text-[13px]">
                      {order.supplier_name}
                    </SupplierLink>
                    {order.origin === "AUTOMATIC" && (
                      <Badge variant="info" size="sm">
                        {t("po.origin.automatic", "Automatic draft")}
                      </Badge>
                    )}
                    {order.origin === "REPLENISHMENT" && (
                      <Badge variant="neutral" size="sm">
                        {t("po.origin.replenishment", "From replenishment")}
                      </Badge>
                    )}
                    {order.is_on_hold && (
                      <Badge variant="warning" size="sm">
                        {t("po.on-hold", "On hold")}
                      </Badge>
                    )}
                  </div>
                  <p className="text-[12px] text-ink-muted">
                    {order.required_date
                      ? t("replenishment.orders.meta", "{{lines}} lines · into {{warehouse}} · needed {{date}}", {
                          lines: order.line_count,
                          // Lets the locale add a "_one" form: "1 line".
                          count: order.line_count,
                          warehouse: order.warehouse_code ?? "",
                          date: order.required_date,
                        })
                      : // No date asked of the supplier: say nothing rather than "needed" and a blank.
                        t("replenishment.orders.meta-undated", "{{lines}} lines · into {{warehouse}}", {
                          lines: order.line_count,
                          // Lets the locale add a "_one" form: "1 line".
                          count: order.line_count,
                          warehouse: order.warehouse_code ?? "",
                        })}
                    {order.submitted_at && status === "AWAITING_APPROVAL" && (
                      <> · {t("replenishment.orders.submitted", "submitted {{date}}", { date: order.submitted_at.slice(0, 10) })}</>
                    )}
                  </p>
                  {order.approval_note && status === "DRAFT" && (
                    <p className="mt-1 rounded-md bg-warn-soft px-2 py-1 text-[12px] text-warn">
                      {t("replenishment.orders.sent-back", "Sent back:")} {order.approval_note}
                    </p>
                  )}
                </div>
                <span className="text-[14px] font-semibold tabular-nums text-ink">{formatCad(order.total_cad)}</span>
                <div className={cn("flex flex-wrap items-center gap-2", !canWrite && "hidden")}>
                  {status === "DRAFT" && (
                    <>
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={order.line_count === 0}
                        loading={issue.isPending && issue.variables.id === order.id && issue.variables.onHold}
                        onClick={() => issue.mutate({ id: order.id, onHold: true })}
                        title={t("replenishment.orders.issue-held.hint", "The supplier gets it but waits to ship until released")}
                      >
                        <PauseCircle className="size-4" />
                        {t("replenishment.orders.issue-held", "Issue on hold")}
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        disabled={order.line_count === 0}
                        loading={issue.isPending && issue.variables.id === order.id && !issue.variables.onHold}
                        onClick={() => issue.mutate({ id: order.id, onHold: false })}
                      >
                        <Send className="size-4" />
                        {t("replenishment.orders.issue", "Issue")}
                      </Button>
                    </>
                  )}
                  {status === "AWAITING_APPROVAL" && canApprove && (
                    <>
                      <Button variant="ghost" size="sm" onClick={() => setSendingBack(order)}>
                        <Undo2 className="size-4" />
                        {t("replenishment.orders.send-back", "Send back")}
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        loading={approve.isPending && approve.variables === order.id}
                        onClick={() => approve.mutate(order.id)}
                      >
                        <CheckCircle2 className="size-4" />
                        {t("replenishment.orders.approve", "Approve")}
                      </Button>
                    </>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
      <SendBackDialog order={sendingBack} onClose={() => setSendingBack(null)} onDone={refresh} />
    </div>
  );
}

function SendBackDialog({ order, onClose, onDone }: { order: PurchaseOrderDTO | null; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const [note, setNote] = useState("");
  // A note typed for one order and cancelled must not be sent with the next.
  useEffect(() => {
    if (order) setNote("");
  }, [order]);
  const send = useMutation({
    mutationFn: () => new PurchaseOrderClient(http).sendBack(order?.id ?? 0, note.trim()),
    onSuccess: (sent) => {
      toast.success(t("replenishment.orders.sent-back.done", "#{{number}} sent back to draft.", { number: sent.number }));
      setNote("");
      onClose();
      onDone();
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });
  return (
    <Dialog
      open={order !== null}
      onClose={onClose}
      title={t("replenishment.orders.send-back.title", "Send #{{number}} back", { number: order?.number ?? "" })}
      description={t("replenishment.orders.send-back.description", "It returns to draft. Say what to change; its writer sees this on the order.")}
      size="sm"
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("dialog.cancel", "Cancel")}
          </Button>
          <Button variant="primary" disabled={note.trim() === ""} loading={send.isPending} onClick={() => send.mutate()}>
            {t("replenishment.orders.send-back", "Send back")}
          </Button>
        </>
      }
    >
      <Field label={t("replenishment.orders.send-back.note", "What to change")} htmlFor="send-back-note" required>
        <Textarea id="send-back-note" rows={3} value={note} onChange={(event) => setNote(event.target.value)} autoFocus />
      </Field>
    </Dialog>
  );
}
